import React, { useEffect, useRef } from 'react';
import { detectLang } from './i18n';
import SPLASH_LOGO from './splashLogo';   // логотип вшит в бандл → появляется сразу

// Все иконки монет (из public/access/coinIcon) — предзагружаем во время сплэша,
// чтобы при входе в приложение они уже были в кэше браузера.
const COIN_ICONS = [
  'btc', 'eth', 'bnb', 'sol', 'xrp', 'ada', 'trx', 'ltc', 'dot', 'avax',
  'link', 'uni', 'aave', 'atom', 'xlm', 'algo', 'xmr', 'apt', 'sui', 'pol',
  'arb', 'inj', 'jup', 'fet', 'crv', 'gala', 'kas', 'hype', 'zora', 'plume',
  'home', 'red', 'era', 'axl', 'alt', 'doge', 'bch', 'near', 'icp', 'etc',
  'hbar', 'fil', 'op', 'vet', 'stx', 'imx', 'grt', 'render', 'tia', 'sei',
  'ldo', 'ondo', 'ena', 'wld', 'tao', 'pendle', 'jto', 'pyth', 'rune', 'wif',
  'pengu', 'trump', 'ar', 'ens', 'dydx', 'shib', 'pepe', 'bonk', 'floki',
  'usdt', 'apex',
];
const EXCHANGE_ICONS = Array.from({ length: 22 }, (_, i) => i);

function buildUrls() {
  const urls = [];
  COIN_ICONS.forEach((i) => urls.push(`/access/coinIcon/${i}.png`));
  EXCHANGE_ICONS.forEach((i) => urls.push(`/access/indexExchange/${i}.svg`));
  urls.push('/access/coinIcon/base.svg');
  urls.push('/access/logo.png');
  return urls;
}

export default function Splash({ onDone, minMs = 3000 }) {
  const netRef = useRef(null);
  const chartRef = useRef(null);
  const uiRef = useRef(null);
  const lineRef = useRef(null);
  const loadFracRef = useRef(0);
  const assetsDoneRef = useRef(false);
  const doneRef = useRef(false);

  // Предзагрузка всех иконок
  useEffect(() => {
    const urls = buildUrls();
    const total = urls.length;
    if (!total) { loadFracRef.current = 1; assetsDoneRef.current = true; return; }
    let loaded = 0;
    urls.forEach((u) => {
      const img = new Image();
      const done = () => {
        loaded += 1;
        loadFracRef.current = loaded / total;
        if (loaded >= total) assetsDoneRef.current = true;
      };
      img.onload = done;
      img.onerror = done;
      img.src = u;
    });
  }, []);

  // Анимация (частицы + свечи + линия) + логика завершения
  useEffect(() => {
    const net = netRef.current;
    const chart = chartRef.current;
    if (!net || !chart) return;
    const nctx = net.getContext('2d');
    const cctx = chart.getContext('2d');
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    let W, H, CW;
    const CH = 120, CPAD = 8, MAXD = 150 * DPR;
    let pts = [];

    const sizeNet = () => {
      W = net.width = window.innerWidth * DPR;
      H = net.height = window.innerHeight * DPR;
      net.style.width = window.innerWidth + 'px';
      net.style.height = window.innerHeight + 'px';
    };
    const sizeChart = () => {
      CW = chart.clientWidth || 300;
      chart.width = CW * DPR;
      chart.height = CH * DPR;
    };
    sizeNet(); sizeChart();
    const onResize = () => { sizeNet(); sizeChart(); };
    window.addEventListener('resize', onResize);

    const N = Math.max(14, Math.min(28, Math.floor(window.innerWidth * window.innerHeight / 24000)));
    for (let i = 0; i < N; i++) {
      pts.push({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.28 * DPR, vy: (Math.random() - 0.5) * 0.28 * DPR,
        r: (Math.random() * 1.6 + 0.8) * DPR,
      });
    }

    // свечи (детерминированная форма)
    const cn = 22;
    const candles = [];
    let p = 0.30;
    for (let i = 0; i < cn; i++) {
      const o = p;
      const c = o + 0.022 + Math.sin(i * 0.9) * 0.05 + Math.sin(i * 2.3) * 0.025;
      const hi = Math.max(o, c) + 0.025 + Math.abs(Math.sin(i * 1.7)) * 0.025;
      const lo = Math.min(o, c) - 0.025 - Math.abs(Math.cos(i * 1.3)) * 0.025;
      candles.push({ o, h: hi, l: lo, c }); p = c;
    }
    let mn = 1, mx = 0;
    candles.forEach((k) => { mn = Math.min(mn, k.l); mx = Math.max(mx, k.h); });
    const rg = (mx - mn) || 1;
    candles.forEach((k) => {
      k.o = 0.08 + (k.o - mn) / rg * 0.84; k.c = 0.08 + (k.c - mn) / rg * 0.84;
      k.h = 0.08 + (k.h - mn) / rg * 0.84; k.l = 0.08 + (k.l - mn) / rg * 0.84;
    });
    const yOf = (v) => CPAD + (1 - v) * (CH - 2 * CPAD);

    const drawChart = (pr) => {
      cctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      cctx.clearRect(0, 0, CW, CH);
      const slotW = (CW - 2 * CPAD) / cn, bodyW = Math.max(2.5, slotW * 0.55), reveal = pr * cn;
      for (let i = 0; i < cn; i++) {
        const vis = reveal - i; if (vis <= 0) break;
        const a = Math.min(1, vis), k = candles[i];
        const x = CPAD + (i + 0.5) * slotW, up = k.c >= k.o, col = up ? '#3ddc97' : '#ff5b6e';
        const yH = yOf(k.h), yL = yOf(k.l), yO = yOf(k.o), yC = yOf(k.c);
        cctx.globalAlpha = a; cctx.strokeStyle = col; cctx.lineWidth = 1.4; cctx.lineCap = 'round';
        cctx.beginPath(); cctx.moveTo(x, yH); cctx.lineTo(x, yL); cctx.stroke();
        const top = Math.min(yO, yC), h = Math.max(2, Math.abs(yC - yO));
        cctx.fillStyle = col; cctx.shadowBlur = 7; cctx.shadowColor = col;
        cctx.fillRect(x - bodyW / 2, top, bodyW, h); cctx.shadowBlur = 0;
      }
      cctx.globalAlpha = 1;
    };

    let raf;
    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const tBase = 1 - Math.pow(1 - Math.min(elapsed / minMs, 1), 3);   // easeOutCubic за minMs
      const ready = assetsDoneRef.current && elapsed >= minMs;
      // прогресс отражает загрузку: ждём на ~92%, если ассеты ещё грузятся
      const progress = ready ? 1 : Math.min(tBase, 0.30 + 0.62 * loadFracRef.current, 0.95);

      if (lineRef.current) lineRef.current.style.width = (progress * 100) + '%';
      drawChart(progress);

      const energy = 0.45 + progress * 0.9;
      nctx.clearRect(0, 0, W, H);
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i]; a.x += a.vx; a.y += a.vy;
        if (a.x < 0 || a.x > W) a.vx *= -1;
        if (a.y < 0 || a.y > H) a.vy *= -1;
      }
      nctx.lineWidth = DPR;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i], b = pts[j], dx = a.x - b.x, dy = a.y - b.y, d = Math.sqrt(dx * dx + dy * dy);
          if (d < MAXD) {
            nctx.strokeStyle = 'rgba(96,189,255,' + ((1 - d / MAXD) * 0.22 * energy) + ')';
            nctx.beginPath(); nctx.moveTo(a.x, a.y); nctx.lineTo(b.x, b.y); nctx.stroke();
          }
        }
      }
      nctx.shadowBlur = 8 * DPR; nctx.shadowColor = 'rgba(96,189,255,.85)';
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i]; nctx.beginPath(); nctx.arc(a.x, a.y, a.r, 0, 7);
        nctx.fillStyle = 'rgba(150,210,255,' + (0.5 + 0.4 * energy) + ')'; nctx.fill();
      }
      nctx.shadowBlur = 0;

      if (ready && !doneRef.current) {
        doneRef.current = true;
        if (uiRef.current) uiRef.current.classList.add('splash-fade');
        if (lineRef.current) lineRef.current.classList.add('splash-fade');
        setTimeout(() => { onDone && onDone(); }, 560);
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, [onDone, minMs]);

  const label = detectLang() === 'ru' ? 'Загрузка терминала' : 'Loading terminal';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, overflow: 'hidden',
      background: 'radial-gradient(125% 90% at 15% 0%, rgba(8,145,178,0.38), transparent 62%), linear-gradient(180deg, #0E1A22 0%, #0B141B 58%, #0A1117 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <style>{`
        .splash-ui { transition: opacity .55s ease; }
        .splash-ui.splash-fade { opacity: 0; }
        .splash-line { transition: opacity .55s ease; }
        .splash-line.splash-fade { opacity: 0; }
        @keyframes splashIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        .s-logo  { animation: splashIn .5s ease .02s both; }
        .s-chart { animation: splashIn .5s ease .08s both; }
        .s-label { animation: splashIn .5s ease .08s both; }
      `}</style>
      <canvas ref={netRef} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
      <div ref={uiRef} className="splash-ui" style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, width: 340, maxWidth: '90vw' }}>
        <img className="s-logo" src={SPLASH_LOGO} alt="APEX DeFi Wallet" style={{ width: 188, maxWidth: '62vw', filter: 'drop-shadow(0 10px 34px rgba(96,189,255,.35))', marginBottom: 2 }} />
        <canvas ref={chartRef} className="s-chart" style={{ display: 'block', width: '100%', height: 120 }} />
        <div className="s-label" style={{ fontSize: 12, letterSpacing: '.34em', textTransform: 'uppercase', color: '#7E8190' }}>{label}</div>
      </div>
      <div ref={lineRef} className="splash-line" style={{ position: 'fixed', left: 0, bottom: 0, height: 3, width: 0, background: 'linear-gradient(90deg,#2E9BF0,#60BDFF,#A9DCFF)', boxShadow: '0 0 16px rgba(96,189,255,.8)', zIndex: 3 }} />
    </div>
  );
}
